const PREPARATION_RETRY_COUNT = 8;
const PREPARATION_RETRY_DELAY_MS = 500;
export const ENCRYPTED_TILE_COUNT = 9;
export const ENCRYPTED_TILE_TAG_LENGTH = 16;

const base64UrlToUint8Array = (value) => {
    const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    const binary = window.atob(padded);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }

    return bytes;
};

export const deriveEncryptedTileUrl = (tileBaseUrl, index) => (
    `${tileBaseUrl}_${index + 1}.aesgcm`
);

export const deriveEncryptedTileIv = (noncePrefix, index) => {
    const prefix = base64UrlToUint8Array(noncePrefix);
    if (prefix.length !== 8 || index < 0 || index >= ENCRYPTED_TILE_COUNT) {
        throw new Error("The encrypted image tile IV is invalid.");
    }

    const iv = new Uint8Array(12);
    iv.set(prefix, 0);
    new DataView(iv.buffer).setUint32(8, index, false);
    return iv;
};

const waitForPreparation = (signal) => new Promise((resolve, reject) => {
    if (signal && signal.aborted) {
        reject(new DOMException("The viewer request was aborted.", "AbortError"));
        return;
    }

    const timer = window.setTimeout(() => {
        if (signal) {
            signal.removeEventListener("abort", onAbort);
        }
        resolve();
    }, PREPARATION_RETRY_DELAY_MS);

    const onAbort = () => {
        window.clearTimeout(timer);
        reject(new DOMException("The viewer request was aborted.", "AbortError"));
    };

    if (signal) {
        signal.addEventListener("abort", onAbort, {once: true});
    }
});

const fetchViewerMetadata = async (url, headers, signal) => {
    for (let attempt = 0; attempt < PREPARATION_RETRY_COUNT; attempt += 1) {
        const response = await fetch(url, {
            headers,
            signal,
            cache: "no-store",
        });

        if (response.status === 409 && attempt < PREPARATION_RETRY_COUNT - 1) {
            await waitForPreparation(signal);
            continue;
        }

        if (!response.ok) {
            throw new Error(`The encrypted viewer metadata request failed (${response.status}).`);
        }

        return response.json();
    }

    throw new Error("The image is still being prepared.");
};

const getDecryptionKey = async (metadata) => {
    if (!window.crypto || !window.crypto.subtle) {
        throw new Error("Web Crypto is not available.");
    }

    return window.crypto.subtle.importKey(
        "raw",
        base64UrlToUint8Array(metadata.key),
        {name: "AES-GCM"},
        false,
        ["decrypt"],
    );
};

const fetchCiphertext = async (url, signal) => {
    const ciphertextResponse = await fetch(url, {
        signal,
        mode: "cors",
    });

    if (!ciphertextResponse.ok) {
        throw new Error(`The encrypted image request failed (${ciphertextResponse.status}).`);
    }

    const contentType = (ciphertextResponse.headers.get("content-type") || "").toLowerCase();
    if (!contentType.startsWith("application/octet-stream")) {
        throw new Error("The encrypted image response has an invalid content type.");
    }

    return new Uint8Array(await ciphertextResponse.arrayBuffer());
};

export const decryptViewerMetadata = async (metadata, signal) => {
    const ciphertext = await fetchCiphertext(metadata.ciphertext_url, signal);
    const tag = base64UrlToUint8Array(metadata.tag);
    const encryptedPayload = new Uint8Array(ciphertext.length + tag.length);
    encryptedPayload.set(ciphertext);
    encryptedPayload.set(tag, ciphertext.length);
    const key = await getDecryptionKey(metadata);
    const plaintext = await window.crypto.subtle.decrypt(
        {
            name: "AES-GCM",
            iv: base64UrlToUint8Array(metadata.iv),
        },
        key,
        encryptedPayload,
    );

    return new Blob([plaintext], {type: metadata.mime});
};

export const decryptTiledViewerMetadata = async (metadata, signal) => {
    if (!Array.isArray(metadata.tiles) || metadata.tiles.length !== ENCRYPTED_TILE_COUNT) {
        throw new Error("The encrypted image tile layout is invalid.");
    }

    const key = await getDecryptionKey(metadata);
    const tiles = await Promise.all(metadata.tiles.map(async (rectangle, index) => {
        const ciphertext = await fetchCiphertext(
            deriveEncryptedTileUrl(metadata.tile_base_url, index),
            signal,
        );
        if (ciphertext.length <= ENCRYPTED_TILE_TAG_LENGTH) {
            throw new Error("The encrypted image tile is invalid.");
        }

        const plaintext = await window.crypto.subtle.decrypt(
            {
                name: "AES-GCM",
                iv: deriveEncryptedTileIv(metadata.nonce_prefix, index),
            },
            key,
            ciphertext,
        );

        return {
            ...rectangle,
            blob: new Blob([plaintext], {type: metadata.mime}),
        };
    }));

    return {
        type: "tiles",
        tiles,
    };
};

const loadViewerImage = async ({metadataUrl, headers, signal}) => {
    const metadata = await fetchViewerMetadata(metadataUrl, headers, signal);

    if (Number(metadata.version) === 2) {
        return decryptTiledViewerMetadata(metadata, signal);
    }

    return decryptViewerMetadata(metadata, signal);
};

export const loadCustomerViewerImage = ({galleryLink, viewerTicket, signal}) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const metadataUrl = `${apiUrl}/usergalery/${encodeURIComponent(galleryLink)}/viewer-key?ticket=${encodeURIComponent(viewerTicket)}`;

    return loadViewerImage({metadataUrl, headers: {}, signal});
};

export const loadAdminViewerImage = ({galeryId, imageId, token, signal}) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const metadataUrl = `${apiUrl}/galery/${encodeURIComponent(galeryId)}/images/${encodeURIComponent(imageId)}/viewer-key`;

    return loadViewerImage({
        metadataUrl,
        headers: {
            Authentication: `Bearer ${token}`,
        },
        signal,
    });
};